import { Model, type ModelProps } from '@/infra/model'
import { Id } from '@/infra/id'
import { action, computed, observable } from 'mobx'
import {
  matchSupportingItemInput,
  type MatchSupportingItemInputArms,
  type SupportingItemInput,
  type SupportingItemInputProps,
} from '@/features/jobs/details/tax-type/info-gathering/supporting-item-input'
import { PersonalInfoSupportingItemInput } from '@/features/jobs/details/tax-type/info-gathering/personal-info/PersonalInfoSupportingItemInput'
import { SpousalInfoSupportingItemInput } from '@/features/jobs/details/tax-type/info-gathering/spousal-info/SpousalInfoSupportingItemInput'
import { DependentsInfoSupportingItemInput } from '@/features/jobs/details/tax-type/info-gathering/dependents-info/DependentsInfoSupportingItemInput'
import { PersonalTaxRefundMethodSupportingItemInput } from '@/features/jobs/details/tax-type/info-gathering/refund-methods/PersonalTaxRefundMethodSupportingItemInput'
import { BusinessInfoSupportingItemInput } from '@/features/jobs/details/tax-type/info-gathering/business-info/BusinessInfoSupportingItemInput'
import { BusinessTaxRefundMethodSupportingItemInput } from '@/features/jobs/details/tax-type/info-gathering/refund-methods/BusinessTaxRefundMethodSupportingItemInput'
import { DocumentSupportingItemInput } from '@/features/jobs/details/tax-type/info-gathering/documents/DocumentSupportingItemInput'
import { NotesSupportingItemInput } from '@/features/jobs/details/tax-type/info-gathering/notes/NotesSupportingItemInput'

export type RequestedSupportingItemProps =
  ModelProps<RequestedSupportingItem> & {
    input: SupportingItemInputProps
  }

/**
 * A requested item on a checklist.
 */
export class RequestedSupportingItem<
  TInput extends SupportingItemInput = SupportingItemInput,
> extends Model<RequestedSupportingItem<TInput>> {
  @observable private accessor _input: SupportingItemInput =
    // We'll need a default value
    new PersonalInfoSupportingItemInput()

  id = Id.none

  @observable accessor name = ''
  @observable accessor heading = ''
  @observable accessor shortDescription = ''
  @observable accessor longDescription = ''
  @observable accessor imageUrl: string | null = null
  @observable accessor previewUrl: string | null = null
  @observable accessor required = false
  @observable accessor adHoc = false

  @computed get input(): TInput {
    return this._input as TInput
  }

  @computed get isCompleted() {
    return Boolean(this.input?.isCompleted)
  }

  /** @inheritDoc */
  @action.bound
  override set({
    input,
    ...props
  }: Partial<RequestedSupportingItemProps>): this {
    super.set(props)
    if (!input) {
      return this
    }

    if (input.type === 'PersonalInfo') {
      if (this._input instanceof PersonalInfoSupportingItemInput) {
        this._input.set(input)
      } else {
        this._input = new PersonalInfoSupportingItemInput().set(input)
      }
    } else if (input.type === 'SpousalInfo') {
      if (this._input instanceof SpousalInfoSupportingItemInput) {
        this._input.set(input)
      } else {
        this._input = new SpousalInfoSupportingItemInput().set(input)
      }
    } else if (input.type === 'DependentsInfo') {
      if (this._input instanceof DependentsInfoSupportingItemInput) {
        this._input.set(input)
      } else {
        this._input = new DependentsInfoSupportingItemInput().set(input)
      }
    } else if (input.type === 'PersonalTaxRefundMethod') {
      if (this._input instanceof PersonalTaxRefundMethodSupportingItemInput) {
        this._input.set(input)
      } else {
        this._input = new PersonalTaxRefundMethodSupportingItemInput().set(
          input,
        )
      }
    } else if (input.type === 'BusinessInfo') {
      if (this._input instanceof BusinessInfoSupportingItemInput) {
        this._input.set(input)
      } else {
        this._input = new BusinessInfoSupportingItemInput().set(input)
      }
    } else if (input.type === 'BusinessTaxRefundMethod') {
      if (this._input instanceof BusinessTaxRefundMethodSupportingItemInput) {
        this._input.set(input)
      } else {
        this._input = new BusinessTaxRefundMethodSupportingItemInput().set(
          input,
        )
      }
    } else if (input.type === 'Document') {
      if (this._input instanceof DocumentSupportingItemInput) {
        this._input.set(input)
      } else {
        this._input = new DocumentSupportingItemInput().set(input)
      }
    } else if (input.type === 'Notes') {
      if (this._input instanceof NotesSupportingItemInput) {
        this._input.set(input)
      } else {
        this._input = new NotesSupportingItemInput().set(input)
      }
    }

    return this
  }

  /**
   * Matches on the input type.
   */
  match<T>(arms: MatchSupportingItemInputArms<T>) {
    return matchSupportingItemInput<T>(this, arms)
  }
}
